<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Finished-Goods Inventory Receipt
            </v-card-title>
            <v-card-text class="pt-4">
              <v-autocomplete
                v-model="form.production_delivery_master_id"
                :items="deliveryList"
                :item-title="deliveryTitle"
                item-value="id"
                label="Approved Production Delivery *"
                density="compact"
                variant="outlined"
                clearable
                :disabled="isEdit"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="changeDelivery"
              />

              <v-autocomplete
                v-model="form.warehouse_id"
                :items="warehouses"
                item-title="name"
                item-value="id"
                label="Destination Warehouse *"
                density="compact"
                variant="outlined"
                disabled
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
              />

              <v-date-input
                v-model="form.receive_date"
                label="Receive Date *"
                input-format="dd/mm/yyyy"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                density="compact"
                variant="outlined"
              />

              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                rows="3"
                density="compact"
                variant="outlined"
              />

              <v-alert type="success" variant="tonal" density="compact">
                On approval, Inventory stock is increased and the finished-goods accounting entry is posted. Production cost components remain separated in Inventory.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Receive Products &amp; Cost Breakdown
            </v-card-title>
            <v-card-text class="pt-4">
              <v-table density="compact" class="border-sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Finished Product</th>
                    <th>Batch / Inventory Lot</th>
                    <th>Manufactured / Expiry</th>
                    <th class="text-end">Pending</th>
                    <th style="width: 135px">Receive Qty</th>
                    <th>Unit Cost Breakdown</th>
                    <th style="width: 125px">Sales Rate</th>
                    <th class="text-end">Amount</th>
                    <th style="width: 45px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cart" :key="item.production_delivery_detail_id">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div class="font-weight-medium">{{ item.product_code }} - {{ item.product_name }}</div>
                      <div class="text-caption text-medium-emphasis">{{ item.unit_name }}</div>
                    </td>
                    <td>
                      <div class="text-caption">Batch: <strong>{{ item.batch_no || '-' }}</strong></div>
                      <v-text-field
                        v-model="item.lot_no"
                        label="Inventory Lot"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                    </td>
                    <td>
                      <div>{{ dateOnly(item.manufacturing_date) || '-' }}</div>
                      <div class="text-caption text-medium-emphasis">Exp: {{ dateOnly(item.expiry_date) || '-' }}</div>
                    </td>
                    <td class="text-end">{{ number(item.pending_quantity) }}</td>
                    <td>
                      <v-text-field
                        v-model.number="item.qty"
                        type="number"
                        min="0.0001"
                        :max="item.pending_quantity"
                        step="0.0001"
                        density="compact"
                        variant="outlined"
                        hide-details
                        @update:model-value="clampQuantity(item)"
                      />
                    </td>
                    <td>
                      <div class="cost-line"><span>Material</span><strong>{{ number(item.material_unit_cost, 6) }}</strong></div>
                      <div class="cost-line"><span>Packaging</span><strong>{{ number(item.packaging_unit_cost, 6) }}</strong></div>
                      <div class="cost-line"><span>Labour</span><strong>{{ number(item.labour_unit_cost, 6) }}</strong></div>
                      <div class="cost-line"><span>Overhead/Other</span><strong>{{ number(item.overhead_unit_cost, 6) }}</strong></div>
                      <div class="cost-line total"><span>Total</span><strong>{{ number(item.rate, 6) }}</strong></div>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="item.sales_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                    </td>
                    <td class="text-end font-weight-medium">{{ number(lineAmount(item), 2) }}</td>
                    <td>
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        color="error"
                        variant="text"
                        @click="cart.splice(index, 1)"
                      />
                    </td>
                  </tr>
                  <tr v-if="cart.length === 0">
                    <td colspan="10" class="text-center text-medium-emphasis py-6">
                      Select an approved Production delivery with pending finished goods.
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="cart.length">
                  <tr class="font-weight-bold">
                    <td colspan="8" class="text-end">Receipt total</td>
                    <td class="text-end">{{ number(receiptTotal, 2) }}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </v-table>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="tonal" :disabled="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" @click="submit(1)">Submit for Approval</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const route = useRoute();
const router = useRouter();

const receiveId = route.params.id || route.query.id || null;
const isEdit = Boolean(receiveId);
const saving = ref(false);
const cart = ref([]);
const deliveryList = ref([]);

const form = reactive({
  production_delivery_master_id: null,
  warehouse_id: null,
  receive_date: new Date(),
  remarks: "",
});

const warehouses = computed(() => store.getters.getWarehouseList || []);
const selectedDelivery = computed(() => deliveryList.value.find(
  (item) => Number(item.id) === Number(form.production_delivery_master_id)
) || null);
const receiptTotal = computed(() => cart.value.reduce((sum, item) => sum + lineAmount(item), 0));

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function deliveryTitle(item) {
  return `${item.code} · ${item.delivery_date || ""} · ${item.production?.code || ""}`;
}

function lineAmount(item) {
  return Number(item.qty || 0) * Number(item.rate || 0);
}

function mapDeliveryDetail(detail) {
  const pending = Math.max(0, Number(detail.quantity || 0) - Number(detail.received_quantity || 0));
  return {
    production_delivery_detail_id: detail.id,
    product_id: detail.product_id,
    product_code: detail.product?.code || "",
    product_name: detail.product?.name || "",
    unit_name: detail.product?.unit?.name || "",
    batch_no: detail.batch_no || detail.production_detail?.batch_no,
    lot_no: detail.batch_no || detail.production_detail?.batch_no || "",
    manufacturing_date: detail.manufacturing_date || detail.production_detail?.manufacturing_date,
    expiry_date: detail.expiry_date || detail.production_detail?.expiry_date,
    pending_quantity: pending,
    qty: pending,
    rate: Number(detail.rate || 0),
    sales_rate: Number(detail.sales_rate || 0),
    material_unit_cost: Number(detail.material_unit_cost || 0),
    packaging_unit_cost: Number(detail.packaging_unit_cost || 0),
    labour_unit_cost: Number(detail.labour_unit_cost || 0),
    overhead_unit_cost: Number(detail.overhead_unit_cost || 0),
  };
}

function changeDelivery() {
  const delivery = selectedDelivery.value;
  if (!delivery) {
    form.warehouse_id = null;
    cart.value = [];
    return;
  }
  form.warehouse_id = delivery.warehouse_id;
  if (delivery.delivery_date && new Date(form.receive_date) < new Date(delivery.delivery_date)) {
    form.receive_date = new Date(delivery.delivery_date);
  }
  cart.value = (delivery.details || []).map(mapDeliveryDetail).filter((item) => item.pending_quantity > 0.0001);
}

function clampQuantity(item) {
  item.qty = Math.min(
    Math.max(0, Number(item.qty || 0)),
    Number(item.pending_quantity || 0)
  );
  item.sales_rate = Math.max(0, Number(item.sales_rate || 0));
}

function validate() {
  if (!form.production_delivery_master_id || !form.warehouse_id || !form.receive_date) {
    return "Approved Production delivery, destination warehouse and receive date are required.";
  }
  if (!cart.value.length) return "At least one finished-product line is required.";
  if (cart.value.some((item) => Number(item.qty || 0) <= 0)) {
    return "Every selected finished product must have a receive quantity greater than zero.";
  }
  return null;
}

async function submit(status) {
  const validationMessage = validate();
  if (validationMessage) {
    toast.warning(validationMessage);
    return;
  }

  const payload = {
    production_receive_master_id: receiveId,
    production_delivery_master_id: form.production_delivery_master_id,
    warehouse_id: form.warehouse_id,
    receive_date: formattedDate(form.receive_date),
    status,
    remarks: form.remarks || null,
    cart: cart.value.map((item) => ({
      production_delivery_detail_id: item.production_delivery_detail_id,
      product_id: item.product_id,
      lot_no: item.lot_no || item.batch_no || null,
      qty: Number(item.qty),
      sales_rate: Number(item.sales_rate || 0),
    })),
  };

  saving.value = true;
  try {
    const response = await store.dispatch("saveUpdateInvProductionReceive", payload);
    toast.success(response?.data?.message || "Finished-goods Inventory receipt saved.");
    router.push({ name: "production-receive-records" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const text = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message;
    toast.error(text || "Finished-goods Inventory receipt could not be saved.");
  } finally {
    saving.value = false;
  }
}

async function loadEditData() {
  const response = await store.dispatch("getInvProductionReceiveById", receiveId);
  const data = response?.data || response;
  const delivery = data.production_delivery;
  if (delivery && !deliveryList.value.some((item) => Number(item.id) === Number(delivery.id))) {
    deliveryList.value.unshift(delivery);
  }
  form.production_delivery_master_id = data.production_delivery_master_id;
  form.warehouse_id = data.warehouse_id;
  form.receive_date = data.receive_date ? new Date(data.receive_date) : new Date();
  form.remarks = data.remarks || "";
  cart.value = (data.details || []).map((detail) => {
    const source = detail.production_delivery_detail || {};
    const pending = Math.max(
      Number(detail.qty || 0),
      Number(source.quantity || detail.qty || 0) - Number(source.received_quantity || 0)
    );
    return {
      production_delivery_detail_id: detail.production_delivery_detail_id,
      product_id: detail.product_id,
      product_code: detail.product?.code || "",
      product_name: detail.product?.name || "",
      unit_name: detail.product?.unit?.name || "",
      batch_no: detail.batch_no,
      lot_no: detail.lot_no || detail.batch_no || "",
      manufacturing_date: detail.manufacturing_date,
      expiry_date: detail.expiry_date,
      pending_quantity: pending,
      qty: Number(detail.qty || 0),
      rate: Number(detail.rate || 0),
      sales_rate: Number(detail.sales_rate || 0),
      material_unit_cost: Number(detail.material_unit_cost || 0),
      packaging_unit_cost: Number(detail.packaging_unit_cost || 0),
      labour_unit_cost: Number(detail.labour_unit_cost || 0),
      overhead_unit_cost: Number(detail.overhead_unit_cost || 0),
    };
  });
}

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch("getPrdDeliveryList", { is_receivable: true }),
      store.dispatch("getWarehouseList"),
    ]);
    deliveryList.value = [...(store.getters.getPrdDeliveryList || [])];
    if (isEdit) await loadEditData();
  } catch (error) {
    toast.error(error?.response?.data?.message || "Receivable Production deliveries could not be loaded.");
  }
});
</script>

<style scoped>
.cost-line {
  display: flex;
  font-size: 0.75rem;
  justify-content: space-between;
  min-width: 155px;
}

.cost-line.total {
  border-top: 1px solid rgba(0, 0, 0, 0.18);
  margin-top: 2px;
  padding-top: 2px;
}
</style>
